import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiChangePassword, apiMe } from '../api';
import { useAuth } from '../context/AuthContext';
import PasswordInput from './PasswordInput';
import { checkPassword, PASSWORD_MAX, PASSWORD_MIN } from '../lib/passwordPolicy';

/**
 * Forced password change at first login (phase alpha, 2026-10-20).
 *
 * Shown when the signed-in account carries `must_change_password` — set by the
 * superadmin's "Set password" action when it CREATES an account. A blocking
 * modal (not dismissible): the account was created by handing over a
 * provisional password, so the user picks their own before using the app.
 * The showcase (demo) account and every account without the flag are untouched.
 *
 * The rules are checked LIVE against the same policy as the server
 * (`lib/passwordPolicy.ts`, mirror of `validatePassword`): an unmet rule stays
 * visible and red, and the submit button stays disabled. The server remains the
 * authority — this only removes the silent round-trip that left users stuck
 * without knowing which criterion their password missed.
 *
 * On success: re-reads /api/auth/me (the server clears the flag) and refreshes
 * the auth context — no reload needed.
 */
export default function MustChangePassword() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!user || user.is_showcase || !user.must_change_password) return null;

  const check = checkPassword(next);
  const canSubmit = !busy && current.length > 0 && check.ok;

  const submit = async () => {
    if (busy) return;
    setError('');
    if (!current || !next) { setError(t('auth.mustChangeFill')); return; }
    if (!check.ok) return;
    setBusy(true);
    try {
      await apiChangePassword(current, next);
      const fresh = await apiMe();
      setUser(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setBusy(false);
    }
  };

  const ruleStyle = (ok: boolean) => ({
    color: ok ? 'var(--success)' : 'var(--danger)',
    fontSize: 'var(--font-xs)',
    margin: '2px 0',
  });

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="must-change-password">
        <div className="modal-header">
          <h3 className="modal-title">{t('auth.mustChangeTitle')}</h3>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-xs)', marginTop: 0 }}>
            {t('auth.mustChangeBody')}
          </p>
          <label>{t('auth.mustChangeCurrent')}</label>
          <PasswordInput className="form-input" value={current}
            onChange={(e) => setCurrent(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
          <label style={{ marginTop: 12 }}>{t('auth.mustChangeNew')}</label>
          <PasswordInput className="form-input" value={next}
            onChange={(e) => setNext(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
          <div style={{ marginTop: 6 }} data-testid="password-rules">
            <p style={ruleStyle(!check.tooShort && !check.tooLong)}>
              {check.tooShort || check.tooLong ? '✗ ' : '✓ '}
              {t('passwordRules.length', { min: PASSWORD_MIN, max: PASSWORD_MAX })}
            </p>
            <p style={ruleStyle(!check.weakMix)}>
              {check.weakMix ? '✗ ' : '✓ '}
              {t('passwordRules.mix')}
            </p>
            <p style={ruleStyle(!check.predictable)}>
              {check.predictable ? '✗ ' : '✓ '}
              {t('passwordRules.predictable')}
            </p>
          </div>
          {error && (
            <p style={{ color: 'var(--danger)', fontSize: 'var(--font-xs)', marginTop: 8 }}>{error}</p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" disabled={!canSubmit} onClick={() => void submit()}>
            {busy ? t('account.saving') : t('auth.mustChangeSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
